"use client";

import React, { useRef, useState, useMemo, useCallback } from "react";
import type { DragEvent } from "react";

// Helper function to format file size
const formatBytes = (bytes: number, decimals = 2): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
};

// File type icon helper
function getFileIcon(file: File) {
  const name = file.name.toLowerCase();
  const type = file.type.toLowerCase();
  if (type === "application/pdf" || name.endsWith(".pdf")) {
    return (
      <svg className="h-5 w-5 shrink-0 text-red-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <path d="M9 15h6" /><path d="M9 11h6" />
      </svg>
    );
  }
  if (type.startsWith("image/") || /\.(png|jpe?g|gif|bmp|tiff?|heic|heif|webp|svg)$/.test(name)) {
    return (
      <svg className="h-5 w-5 shrink-0 text-blue-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <polyline points="21 15 16 10 5 21" />
      </svg>
    );
  }
  return (
    <svg className="h-5 w-5 shrink-0 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  );
}

interface DropzoneProps {
  /** Callback when the file list changes (add/remove/reorder). */
  onFilesChanged: (files: File[]) => void;
  /** Current files managed by the parent. */
  files: File[];
  /** Accepted file types (e.g., "image/*,application/pdf"). */
  accept?: string;
  /** Max size per file in MB. */
  maxSizeMB?: number;
  /** Max total number of files. */
  maxFiles?: number;
  /** Show the file list inside the dropzone. @default true */
  showFiles?: boolean;
  /** Content shown in the empty state (before any files are added). */
  children?: React.ReactNode;
  /** Extra CSS classes on the outer container. */
  className?: string;
  /** Disable all interactions. */
  disabled?: boolean;
  /**
   * Custom "full" state: prevents adding more files.
   * Use together with `fullMessage` to explain *why*.
   */
  isFull?: boolean;
  /**
   * Message shown when `isFull` is true (e.g. "Only one PDF allowed").
   * Falls back to a generic "Maximum N files reached" when omitted.
   */
  fullMessage?: string;
}

// Helper to reorder files array
function reorderFiles(files: File[], fromIndex: number, toIndex: number): File[] {
  const updated = [...files];
  const [moved] = updated.splice(fromIndex, 1);
  updated.splice(toIndex, 0, moved);
  return updated;
}

export function Dropzone({
  onFilesChanged,
  files,
  accept = "image/*,application/pdf",
  maxSizeMB = 10,
  maxFiles,
  showFiles = true,
  children,
  className = "",
  disabled = false,
  isFull = false,
  fullMessage,
}: DropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draggedFileIndex, setDraggedFileIndex] = useState<number | null>(null);

  // Whether the dropzone can accept additional files
  const maxFilesReached = maxFiles !== undefined && files.length >= maxFiles;
  const canAddMore = !disabled && !isFull && !maxFilesReached;

  // Determine status message when full
  const statusMessage = useMemo(() => {
    if (disabled) return null;
    if (files.length === 0) return null;
    if (isFull && fullMessage) return fullMessage;
    if (maxFilesReached && maxFiles !== undefined) {
      return maxFiles === 1
        ? "Maximum one file."
        : `${files.length}/${maxFiles} files selected.`;
    }
    if (isFull) {
      return "Cannot add more files.";
    }
    return null;
  }, [disabled, files.length, isFull, fullMessage, maxFilesReached, maxFiles]);

  const inputMultiple = maxFiles !== 1;

  const handleFiles = useCallback(
    (fileList: FileList | null) => {
      if (!fileList || fileList.length === 0) {
        setError(null);
        return;
      }

      const incoming = Array.from(fileList);
      const valid: File[] = [];
      const availableSlots =
        maxFiles === undefined ? Infinity : maxFiles - files.length;

      // Take only what fits
      const batch = incoming.slice(0, availableSlots);

      if (incoming.length > availableSlots) {
        setError(
          availableSlots === 0
            ? "No more files can be added."
            : `Only ${availableSlots} more file(s) can be added. Extra files were ignored.`
        );
      }

      for (const file of batch) {
        if (maxSizeMB !== undefined && file.size > maxSizeMB * 1024 * 1024) {
          setError(`"${file.name}" exceeds the ${maxSizeMB} MB limit.`);
          continue; // skip this file but keep processing others
        }
        valid.push(file);
      }

      if (valid.length > 0) {
        if (!error && incoming.length <= availableSlots) setError(null);
        onFilesChanged([...files, ...valid]);
      }

      // Always reset input so re-selecting the same file works
      if (inputRef.current) inputRef.current.value = "";
    },
    [files, maxFiles, maxSizeMB, onFilesChanged, error]
  );

  const onDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setDragActive(false);
      if (canAddMore) {
        handleFiles(e.dataTransfer.files);
      }
    },
    [canAddMore, handleFiles]
  );

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleFiles(e.target.files);
    },
    [handleFiles]
  );

  const handleRemoveFile = useCallback(
    (fileToRemove: File) => {
      onFilesChanged(files.filter((f) => f !== fileToRemove));
      setError(null);
    },
    [files, onFilesChanged]
  );

  // --- Drag reorder handlers ---
  const handleDragStart =
    (index: number) => (e: React.DragEvent<HTMLLIElement>) => {
      setDraggedFileIndex(index);
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", index.toString());
    };
  const handleDragOverItem =
    (index: number) => (e: React.DragEvent<HTMLLIElement>) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
    };
  const handleDropReorder =
    (index: number) => (e: React.DragEvent<HTMLLIElement>) => {
      e.preventDefault();
      e.stopPropagation();
      let fromIndex = draggedFileIndex;
      if (fromIndex === null) {
        const data = e.dataTransfer.getData("text/plain");
        if (data) fromIndex = parseInt(data, 10);
      }
      if (fromIndex !== null && fromIndex !== index) {
        onFilesChanged(reorderFiles(files, fromIndex, index));
      }
      setDraggedFileIndex(null);
    };
  const handleDragEnd = () => setDraggedFileIndex(null);

  // --- Click handler for the dropzone area ---
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      // Don't open file picker if user clicked a button (e.g. remove)
      if ((e.target as HTMLElement).closest("button")) return;
      if (canAddMore) inputRef.current?.click();
    },
    [canAddMore]
  );

  // --- File counter ---
  const fileCounter =
    maxFiles !== undefined && files.length > 0 ? (
      <span className="text-xs text-muted-foreground">
        {files.length}/{maxFiles} files
      </span>
    ) : null;

  return (
    <div
      className={[
        "relative flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 transition-colors duration-200",
        canAddMore ? "cursor-pointer" : files.length > 0 ? "cursor-default" : "cursor-not-allowed",
        dragActive && canAddMore
          ? "border-primary bg-primary/5"
          : "border-input",
        canAddMore && !disabled
          ? "hover:border-primary/60"
          : "",
        disabled ? "opacity-60 pointer-events-none" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      onClick={handleClick}
      onDragOver={(e) => {
        if (canAddMore) {
          e.preventDefault();
          setDragActive(true);
        }
      }}
      onDragLeave={(e) => {
        e.preventDefault();
        setDragActive(false);
      }}
      onDrop={onDrop}
      tabIndex={canAddMore ? 0 : -1}
      role="region"
      aria-label="File upload area"
      aria-disabled={disabled}
      onKeyDown={(e) => {
        if ((e.key === "Enter" || e.key === " ") && canAddMore) {
          e.preventDefault();
          inputRef.current?.click();
        }
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={inputMultiple}
        className="hidden"
        onChange={onChange}
        tabIndex={-1}
        disabled={!canAddMore}
      />

      {/* === Empty state === */}
      {files.length === 0 && (
        children ?? (
          <div className="flex flex-col items-center text-center gap-1">
            <span className="text-sm text-muted-foreground">
              Drag & drop files here or{" "}
              <span className="underline font-medium">browse</span>
            </span>
            {accept && (
              <span className="text-xs text-muted-foreground/70">
                Accepted: {accept}
              </span>
            )}
            {(maxSizeMB !== undefined || maxFiles !== undefined) && (
              <span className="text-xs text-muted-foreground/70">
                {maxSizeMB !== undefined && `Max ${maxSizeMB} MB per file`}
                {maxSizeMB !== undefined && maxFiles !== undefined && " · "}
                {maxFiles !== undefined && `Up to ${maxFiles} files`}
              </span>
            )}
          </div>
        )
      )}

      {/* === File list === */}
      {showFiles && files.length > 0 && (
        <div className="flex w-full flex-col gap-3">
          <ul className="flex w-full flex-col gap-2">
            {files.map((file, index) => (
              <li
                key={`${file.name}-${file.size}-${index}`}
                className={[
                  "group flex items-center gap-3 rounded-md border bg-muted/40 px-3 py-2 text-sm transition-colors",
                  draggedFileIndex === index ? "opacity-50" : "",
                  !disabled ? "hover:bg-muted/70" : "",
                ].join(" ")}
                draggable={!disabled && files.length > 1}
                onDragStart={handleDragStart(index)}
                onDragOver={handleDragOverItem(index)}
                onDrop={handleDropReorder(index)}
                onDragEnd={handleDragEnd}
                style={{
                  cursor: disabled
                    ? "not-allowed"
                    : files.length > 1
                      ? "grab"
                      : "default",
                }}
              >
                {getFileIcon(file)}
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium leading-tight">
                    {file.name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatBytes(file.size)}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!disabled) handleRemoveFile(file);
                  }}
                  className="ml-auto shrink-0 rounded-full p-1 text-muted-foreground transition-all hover:bg-destructive/10 hover:text-destructive focus:outline-none focus:ring-2 focus:ring-ring"
                  aria-label={`Remove ${file.name}`}
                  disabled={disabled}
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </li>
            ))}
          </ul>

          {/* Footer: counter + status */}
          <div className="flex items-center justify-between gap-2">
            {fileCounter}
            {canAddMore && (
              <span className="text-xs text-muted-foreground">
                Drop more files or{" "}
                <span className="underline cursor-pointer">click to add</span>
              </span>
            )}
            {!canAddMore && statusMessage && (
              <span className="text-xs text-muted-foreground">
                {statusMessage}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Drag overlay */}
      {dragActive && canAddMore && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-lg bg-primary/5">
          <span className="text-sm font-medium text-primary">
            Drop files here
          </span>
        </div>
      )}

      {/* Error */}
      {error && (
        <p className="mt-2 text-center text-xs text-destructive">{error}</p>
      )}
    </div>
  );
}